/**
 * Chat state — conversation-aware chat with server-side persistence.
 *
 * Progressive degradation: falls back to localStorage when API is unavailable.
 */

import { makeAutoObservable, runInAction } from 'mobx';
import { parseReference, splitAnchorBlock } from '@skjv/common';
import type { ConversationSummary } from '@skjv/common';
import { getVerseText } from '@/services/verse-text';
import {
  saveChatMessages, loadChatMessages,
  saveActiveConversation, loadActiveConversation,
} from '../persistence';
import { submitFingerprintOnce } from '../persistence/device-id';
import type { ChatMessage } from './types';
import type { StoredCommentary } from './commentary-history';
import {
  loadConversations, selectConversation, startNewConversation, deleteConversation,
} from './conversation-ops';
import { pingPipeline } from './ping-handler';
import { requestCommentary, retryCommentary } from './commentary-handler';
import { sendMessage } from './send-message';
import { addVerseStudy } from './verse-study-handler';
import { addPericopeStudy } from './pericope-study-handler';

const MAX_LOCAL_MESSAGES = 50;

export class ChatState {
  messages: ChatMessage[] = [];
  conversations: ConversationSummary[] = [];
  activeConversationId: string | null = null;
  isLoading = false;
  isConversationsLoaded = false;
  isConversationListVisible = false;
  isCommentaryHistoryVisible = false;
  /** Free-typed composer visibility — shown for user-initiated new chats
   *  (plus button / panel opened onto a fresh conversation), hidden for
   *  programmatic study flows (verse study, pericope study, recalled
   *  commentary), which have no free-typed follow-up. */
  isComposerVisible = false;

  private messageCounter = 0;

  constructor() {
    makeAutoObservable(this);
    this.messages = loadChatMessages<ChatMessage>();
    this.activeConversationId = loadActiveConversation();
    submitFingerprintOnce();
  }

  nextId(): string {
    return `msg-${Date.now()}-${++this.messageCounter}`;
  }

  async loadConversations(): Promise<void> { return loadConversations(this); }
  async selectConversation(id: string): Promise<void> { return selectConversation(this, id); }
  async startNewConversation(): Promise<void> {
    this.isComposerVisible = true;
    return startNewConversation(this);
  }
  async deleteConversation(id: string): Promise<void> {
    await deleteConversation(this, id);
    // Deleting down to an empty chat leaves the prompt available.
    if (this.messages.length === 0) this.isComposerVisible = true;
  }
  async sendMessage(content: string): Promise<void> { return sendMessage(this, content); }
  async addVerseStudy(reference: string): Promise<void> {
    return addVerseStudy(this, reference);
  }
  async addPericopeStudy(reference: string): Promise<void> {
    return addPericopeStudy(this, reference);
  }

  /** Show the composer when the panel opens onto a fresh conversation. */
  showComposerIfNewChat(): void {
    if (this.messages.length === 0) this.isComposerVisible = true;
  }

  /** The draft component signals the edit theater finished playing —
   *  complete-application waits on this before the typeset. */
  markTheaterDone(messageId: string): void {
    const idx = this.messages.findIndex((m) => m.id === messageId);
    if (idx !== -1) this.messages[idx] = { ...this.messages[idx], theaterDone: true };
  }

  clearHistory(): void {
    this.messages = [];
    this.activeConversationId = null;
    saveActiveConversation(null);
    this.persistLocal();
    // An emptied chat is a fresh chat — the prompt must be ready, never an
    // empty pane (UAT 2026-08-27).
    this.isComposerVisible = true;
  }

  async pingPipeline(): Promise<void> {
    return pingPipeline(
      this.messages,
      (v) => { this.isLoading = v; },
      () => this.persistLocal(),
      () => this.nextId(),
    );
  }

  async requestCommentary(messageId: string, readerThoughts?: string): Promise<void> {
    return requestCommentary(
      this.messages, () => this.persistLocal(), () => this.nextId(), messageId, readerThoughts,
    );
  }

  async retryCommentary(failedMessageId: string): Promise<void> {
    return retryCommentary(
      this.messages, () => this.persistLocal(), () => this.nextId(), failedMessageId,
    );
  }

  toggleConversationList(): void {
    this.isConversationListVisible = !this.isConversationListVisible;
    if (this.isConversationListVisible) this.isCommentaryHistoryVisible = false;
    if (this.isConversationListVisible && !this.isConversationsLoaded) {
      this.loadConversations();
    }
  }

  hideConversationList(): void {
    this.isConversationListVisible = false;
  }

  toggleCommentaryHistory(): void {
    this.isCommentaryHistoryVisible = !this.isCommentaryHistoryVisible;
    if (this.isCommentaryHistoryVisible) this.isConversationListVisible = false;
  }

  hideCommentaryHistory(): void {
    this.isCommentaryHistoryVisible = false;
  }

  /** Load a stored commentary (history entry or a study's attached
   *  commentary) into the viewer as a recalled bubble — no generation run. */
  showStoredCommentary(entry: StoredCommentary): void {
    this.clearHistory();
    // clearHistory readies the composer for a fresh chat; a programmatic
    // recall keeps it hidden (same rule as the study flows).
    this.isComposerVisible = false;
    const recalledId = this.nextId();
    // Entries recorded before passageVerses existed pill their anchors
    // forever — backfill the verse text from the chapter HTML so recalled
    // commentary heals to scripture-form anchors (UAT 2026-08-27). Entries
    // without a pericope (study-attached commentary, oldest history) derive
    // the range from the reference's book+chapter and the anchors' span.
    if (!entry.passageVerses?.length) {
      const range = entry.pericope ?? rangeFromCommentary(entry.reference, entry.text);
      if (range) void this.backfillPassageVerses(recalledId, range);
    }
    this.messages.push({
      id: recalledId,
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      commentary: entry.text,
      rawModelBody: entry.rawModelBody,
      reference: entry.reference,
      itIsWritten: entry.itIsWritten,
      pericope: entry.pericope ?? null,
      intent: entry.intent ?? 'pericope_study',
      passageVerses: entry.passageVerses,
      readerThoughts: entry.readerThoughts,
      reportMeta: entry.reportMeta,
      originalQuery: `Study the passage ${entry.reference}`,
      recalledFrom: {
        pericopeTitle: entry.pericopeTitle,
        reference: entry.reference,
        generatedAt: entry.generatedAt,
      },
    });
    this.persistLocal();
  }

  /** Fetch the range's per-verse KJV from the chapter HTML and patch it
   *  onto the recalled message — anchors upgrade from pills to scripture. */
  private async backfillPassageVerses(messageId: string, pericope: VerseRange): Promise<void> {
    const verses: Array<{ verse: number; text: string }> = [];
    for (let v = pericope.startVerse; v <= pericope.endVerse; v++) {
      const location = parseReference(`${pericope.book} ${pericope.chapter}:${v}`);
      if (!location) return;
      const text = await getVerseText(location).catch(() => '');
      if (text) verses.push({ verse: v, text });
    }
    if (verses.length === 0) return;
    runInAction(() => {
      const idx = this.messages.findIndex((m) => m.id === messageId);
      if (idx === -1) return;
      this.messages[idx] = { ...this.messages[idx], passageVerses: verses };
      this.persistLocal();
    });
  }

  persistLocal(): void {
    const toSave = this.messages
      .filter((m) => !m.isLoading)
      .slice(-MAX_LOCAL_MESSAGES);
    saveChatMessages(toSave);
  }

  updateConversationInList(userMessage: string): void {
    const conv = this.conversations.find((c) => c.id === this.activeConversationId);
    if (conv) {
      conv.updatedAt = new Date().toISOString();
      conv.messageCount = this.messages.filter((m) => !m.isLoading).length;
      conv.lastMessage = userMessage;
      if (conv.title === 'New conversation') {
        conv.title = userMessage.length > 80
          ? userMessage.slice(0, 79) + '\u2026'
          : userMessage;
      }
    }
  }
}

interface VerseRange {
  book: string;
  chapter: number;
  startVerse: number;
  endVerse: number;
}

/**
 * Derive a backfillable verse range for a commentary that carries no
 * pericope: book+chapter from the reference ("John 4:26-44" → John 4),
 * span from the anchors actually present in the text — the reference may
 * name a single verse while the anchors cover the whole pericope.
 */
function rangeFromCommentary(reference: string | undefined, text: string): VerseRange | null {
  const ref = reference?.match(/^(.+?)\s+(\d+):/);
  if (!ref) return null;
  const anchors = text
    .split(/\n{2,}/)
    .map((block) => splitAnchorBlock(block.trim()).anchor)
    .filter((anchor): anchor is NonNullable<typeof anchor> => anchor !== null);
  if (anchors.length === 0) return null;
  return {
    book: ref[1],
    chapter: Number(ref[2]),
    startVerse: Math.min(...anchors.map((a) => a.start)),
    endVerse: Math.max(...anchors.map((a) => a.end)),
  };
}
